import { useCallback, useEffect, useRef, useState } from 'react';
import { storeService } from '../services/storeService';
import { StoreCatalogItem } from '../models';

const AUTO_ADVANCE_INTERVAL_MS = 5000;

export function useHeroShowcase() {
  const [products, setProducts] = useState<StoreCatalogItem[]>([]);
  const [collectionName, setCollectionName] = useState<string | null>(null);
  const [collectionDescription, setCollectionDescription] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setError(null);

    storeService
      .getHeroShowcase()
      .then((data) => {
        if (!active) return;
        setProducts(data.products);
        setCollectionName(data.collectionName);
        setCollectionDescription(data.collectionDescription);
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err : new Error('Erro ao carregar vitrine do banner'));
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const isEmpty = products.length === 0;
  const hasMultiple = products.length > 1;
  const currentProduct = products[currentIndex] ?? null;

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? products.length - 1 : prev - 1));
  }, [products.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === products.length - 1 ? 0 : prev + 1));
  }, [products.length]);

  const isPausedRef = useRef(false);
  const pauseAutoAdvance = useCallback(() => { isPausedRef.current = true; }, []);
  const resumeAutoAdvance = useCallback(() => { isPausedRef.current = false; }, []);

  useEffect(() => {
    if (!hasMultiple) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const id = window.setInterval(() => {
      if (!isPausedRef.current) goToNext();
    }, AUTO_ADVANCE_INTERVAL_MS);

    return () => window.clearInterval(id);
  }, [hasMultiple, goToNext]);

  return {
    currentProduct,
    currentIndex,
    total: products.length,
    hasMultiple,
    goToPrev,
    goToNext,
    pauseAutoAdvance,
    resumeAutoAdvance,
    isLoading,
    error,
    isEmpty,
    isReady: !isLoading && !error && !isEmpty && !!currentProduct,
    collectionName,
    collectionDescription,
  };
}
